import { Button as AntdButton, Dropdown, Space, type MenuProps } from 'antd'
import { observer } from 'mobx-react-lite'
import { Fragment, useEffect, useState, type ReactNode } from 'react'
import { IoMdMore } from 'react-icons/io'

import { configManager } from '@/Core/Managers/Config/ConfigManager'
import type { ButtonGroupButton, ButtonGroupProps } from '@/Core/UI/ButtonGroup/types'
import { useCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { useDrawerContext } from '@/Core/UI/Drawer/DrawerContextProvider'
import { useFormContext } from '@/Core/UI/Form/Providers/FormContextProvider'
import { useHeaderPageContext } from '@/Core/UI/HeaderPage/HeaderPageContextProvider'
import { useListContext } from '@/Core/UI/List/ListContextProvider'

import '@/Core/UI/ButtonGroup/ButtonGroup.css'

/** Службові поля кнопки групи — у DOM/antd не передаються */
const SERVICE_KEYS = ['depFn', 'accessFn', 'depStore', 'sort', 'priority', 'variant'] as const

interface ButtonVariantItem {
    props?: Record<string, unknown>
}

/**
 * Пропси кнопки з урахуванням варіанту з ButtonConfig — як Core Button через UIManager:
 * пропси варіанту (style, type, hover-обробники) накладаються поверх пропсів кнопки
 */
const withVariant = (variant: unknown, props: Record<string, unknown>): Record<string, unknown> => {
    const config = configManager.get('ButtonConfig', {}) as {
        variant?: string
        variantItems?: Record<string, ButtonVariantItem>
    }
    const key = typeof variant === 'string' ? variant : config.variant
    const item = key ? config.variantItems?.[key] : undefined
    return { ...props, ...(item?.props ?? {}) }
}

const resolveLoading = (loading: ButtonGroupButton['loading']) =>
    typeof loading === 'function' ? (loading as () => boolean)() : loading

/**
 * Група кнопок шапки (сторінки й картки) — заміна Core ButtonGroup через alias у vite.config.
 *
 * Чому не ядро: там стор створюється на кожен рендер, внутрішній observer-компонент
 * оголошений усередині функції, а Core Button рендериться через UIManager, що теж створює
 * новий observer-компонент щоразу. Після будь-якого оновлення картки кнопки монтувались
 * наново: блимали, «стрибали» в меню й назад, відкрите меню «⋮» закривалось.
 *
 * Тут компонент один і стабільний, кнопки — antd Button з варіантами з ButtonConfig, набір
 * кнопок береться з пропсів на кожен рендер (свіжі label/loading/disabled/onClick).
 *
 * Відмінності від ядра:
 * - без адаптивного перенесення в меню за шириною (у ядрі вмикалось лише після зміни ширини
 *   вікна й давало стрибки): у шапці — priority 'button', у «⋮» — 'dropdown'; кнопки, що
 *   не вміщаються, переносяться на новий рядок;
 * - «⋮» відкривається кліком, а не наведенням; пункт із loading — неактивний.
 * API (props, формат кнопок, depFn/accessFn, children, dropdown, dropdownButton) — як у ядра.
 * Той самий фікс варто віддати в ядро; після оновлення ядра alias і цю копію прибрати.
 */
export const ButtonGroup = observer(
    ({ buttons, content = [], dropdownButtonIcon, mobileWidth = 670 }: ButtonGroupProps) => {
        const formContext = useFormContext()
        const crudContext = useCrudResourceContext()
        const listContext = useListContext()
        const drawerContext = useDrawerContext()
        const headerPageContext = useHeaderPageContext()

        const [isMobile, setIsMobile] = useState(() => window.innerWidth <= mobileWidth)

        useEffect(() => {
            const onResize = () => setIsMobile(window.innerWidth <= mobileWidth)
            window.addEventListener('resize', onResize)
            return () => window.removeEventListener('resize', onResize)
        }, [mobileWidth])

        // Видимі кнопки (depFn/accessFn) у порядку sort; observer перераховує при зміні їхніх даних
        const visible: ButtonGroupButton[] = []
        Object.keys(buttons).forEach((key) => {
            const button = buttons[key]
            if (!button) return
            const options = {
                formContext,
                crudContext,
                listContext,
                drawerContext,
                headerPageContext,
                ...(button.depStore ?? {}),
            }
            try {
                if (typeof button.depFn === 'function' && button.depFn(options) === false) return
                if (typeof button.accessFn === 'function' && button.accessFn(options) === false) return
            } catch (error) {
                console.error('ButtonGroup depFn/accessFn error:', error)
                return
            }
            visible.push({ ...button, key: button.key || key })
        })
        visible.sort((a, b) => (a.sort ?? 100) - (b.sort ?? 100))

        const inline = visible.filter((btn) => (btn.priority ?? 'button') === 'button')
        const inMenu = visible.filter((btn) => btn.priority === 'dropdown')

        const renderButton = (btn: ButtonGroupButton): ReactNode => {
            const { children, dropdown, dropdownButton, key, label, loading } = btn
            if (children) return <Fragment key={key}>{children}</Fragment>

            const rest: Record<string, unknown> = { ...btn }
            for (const name of [...SERVICE_KEYS, 'children', 'dropdown', 'dropdownButton', 'key', 'label', 'loading']) {
                delete rest[name]
            }
            const loadingValue = resolveLoading(loading)
            const props = withVariant(btn.variant, {
                className: 'button-group-item',
                size: 'middle',
                // На мобільному — лише іконка, підпис у нативному title (без Tooltip: на тач-
                // пристроях тап діставався тултипу, а не кнопці)
                ...(isMobile && typeof label === 'string' ? { title: label } : {}),
                ...(loadingValue !== undefined ? { loading: loadingValue } : {}),
                ...rest,
            })
            const text = !isMobile ? label : null

            if (typeof dropdownButton === 'object') {
                return (
                    <Dropdown.Button key={key} {...(props as any)} {...(dropdownButton as any)}>
                        {text}
                    </Dropdown.Button>
                )
            }
            const buttonRender = (
                <AntdButton key={key} {...(props as any)}>
                    {text}
                </AntdButton>
            )
            if (typeof dropdown === 'object') {
                return (
                    <Dropdown key={key} trigger={['click']} {...dropdown}>
                        {buttonRender}
                    </Dropdown>
                )
            }
            return buttonRender
        }

        const menuItems: NonNullable<MenuProps['items']> = inMenu.map((btn) => ({
            key: String(btn.key),
            label: btn.label,
            icon: btn.icon,
            danger: (btn.variant as string) === 'danger' || btn.danger === true,
            disabled: btn.disabled === true || !!resolveLoading(btn.loading),
            onClick: ({ domEvent }) => btn.onClick?.(domEvent as any),
        }))

        return (
            <>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'end' }} className="wl-button-group">
                    <Space wrap style={{ justifyContent: 'flex-end' }}>
                        {inline.map(renderButton)}
                        {menuItems.length > 0 && (
                            <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                                <AntdButton
                                    icon={dropdownButtonIcon ?? <IoMdMore style={{ fontSize: 22 }} />}
                                    className="button-group-item"
                                    size="middle"
                                    aria-label="Інші дії"
                                    title="Інші дії"
                                />
                            </Dropdown>
                        )}
                    </Space>
                </div>
                {content.map((node, index) => (
                    <Fragment key={index}>{node}</Fragment>
                ))}
            </>
        )
    }
)

export default ButtonGroup
